import type { ReactElement } from 'react'
import {
  faBuildingColumns,
  faCheck,
  faFileArrowDown,
  faHandshake,
  faLaptop,
  faMugHot,
} from '@fortawesome/pro-regular-svg-icons'

import {
  Button,
  Card,
  CardColors,
  Icon,
  ListCustomBullets,
  Separator,
  Text,
} from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { HostAvatars } from './HostAvatars'

const TIPS = [
  {
    icon: faBuildingColumns,
    heading: 'Have your banking logins ready',
    description: 'For every account and card you use for your practice.',
  },
  {
    icon: faFileArrowDown,
    heading: 'Know where to find your statements',
    description: "In case your bank can't send them.",
  },
  {
    icon: faHandshake,
    heading: 'Wait to connect your accounts',
    description: "We'll do it together.",
  },
  {
    icon: faLaptop,
    heading: 'Join from a laptop',
    description: 'So you can share your screen.',
  },
]

export const BookedView = ({
  firstName,
  email,
  sessionTime,
  onReschedule,
  onSkip,
}: {
  firstName: string
  email: string
  sessionTime: string | null
  onReschedule: (() => void) | null
  onSkip: () => void
}): ReactElement => (
  <div className="w-full max-w-200 mx-auto px-4 pt-10 pb-10 flex flex-col gap-8">
    <div className="flex flex-col items-center text-center">
      <Icon
        icon={faCheck}
        size="xl"
        backgroundColor="emphasis"
        color="text-moss-300"
      />
      <Text
        variant={TypographyStyles.DISPLAY_SM}
        textAlign="center"
        className="mt-4"
      >
        You&apos;re booked, {firstName}
      </Text>
      <Text
        variant={TypographyStyles.BODY_LG}
        color={TextColors.MUTED}
        textAlign="center"
        className="mt-3"
      >
        Your invite and video link are on the way to {email}.
      </Text>
    </div>

    <Card
      color={CardColors.LEVEL_0}
      className="my-0"
      childrenClassName="flex flex-col gap-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Text variant={TypographyStyles.EYEBROW} color={TextColors.MOSS}>
            Live onboarding
          </Text>
          <Text variant={TypographyStyles.H3}>
            {sessionTime ?? 'Your session is booked'}
          </Text>
          <div className="flex items-center gap-3">
            <HostAvatars size="sm" />
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              45 min · Video call · with Robby and Ali
            </Text>
          </div>
        </div>
        {onReschedule && (
          <Button variant="secondary" size="sm" onClick={onReschedule}>
            Reschedule
          </Button>
        )}
      </div>

      <Separator />

      <div className="flex flex-col gap-4">
        <Text variant={TypographyStyles.LABEL_LG}>Before the call</Text>
        <ListCustomBullets variant="icon">
          {TIPS.map((tip) => (
            <ListCustomBullets.Item
              key={tip.heading}
              icon={tip.icon}
              iconColor="text-moss-300"
              iconBackgroundColor="emphasis"
              heading={tip.heading}
              description={tip.description}
            />
          ))}
        </ListCustomBullets>
      </div>

      <div className="flex items-center gap-3 rounded-2xl bg-moss-25 p-4">
        <Icon icon={faMugHot} color="text-moss-300" className="shrink-0" />
        <Text variant={TypographyStyles.BODY_SM}>
          Coffee&apos;s on us. Your $15 Starbucks gift card arrives by email
          after the session.
        </Text>
      </div>
    </Card>

    <div className="flex flex-col items-center gap-2">
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        textAlign="center"
      >
        You&apos;re all set. Your invite has everything you need, so you can
        close this page.
      </Text>
      <Button variant="link" size="sm" onClick={onSkip}>
        I&apos;ll set up on my own
      </Button>
    </div>
  </div>
)
